import styled from '@emotion/styled';
import { Alert, notification, theme } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppError, Errors } from '../../state';

// Default exception codes that shouldn't trigger notifications
const ExceptionCode = ['CONFLICT'];
interface ErrorMessageBoxProps {
  errors: Errors; // Array of error objects
  exceptionCode?: string[]; // Custom exception codes to exclude from notifications
  type?: 'layout' | 'notification'; // Display type (inline alert or notification)
  onClose?: () => void; // Callback when error is dismissed
}

const StyledAlert = styled(Alert)`
  margin-bottom: 16px;
  border-width: 1px;
  border-style: solid;
`;

/**
 * A flexible error display component that can show errors as:
 * - Inline alerts (for form validation)
 * - Notification toasts (for global errors)
 * Features:
 * - Automatic error deduplication
 * - Smooth transitions between errors
 * - Customizable exception handling
 * - i18n support
 */
export const ErrorMessageBox: React.FC<ErrorMessageBoxProps> = ({
  errors,
  exceptionCode,
  type = 'notification',
  onClose,
}) => {
  const { t } = useTranslation('errors');
  const oldError = useRef<AppError>();
  const oldErrors = useRef<ErrorMessageBoxProps['errors']>([]);
  const [error, setError] = useState<AppError>();

  const [api, contextHolder] = notification.useNotification();

  const {
    token: { colorError },
  } = theme.useToken();

  // Combine default and custom exception codes
  const excCodes = exceptionCode || ExceptionCode;

  /**
   * Updates the current error with a smooth transition
   * @param e - The new error to display
   */
  const handleChangeError = (e: AppError | undefined) => {
    oldError.current = e;
    setError(e);
  };

  const handleClose = () => {
    handleChangeError(undefined);
    onClose?.();
  };

  // Effect to handle error changes and transitions
  useEffect(() => {
    // Find the first changed error
    const e = errors.find((value, index) => value !== oldErrors.current[index]);
    oldErrors.current = errors;

    if (!!oldError.current && e) {
      // If we have a current error and a new one, fade out first
      handleChangeError(undefined);
      const handler = setTimeout(() => handleChangeError(e), 500);

      return () => {
        clearTimeout(handler);
      };
    } else {
      // Otherwise show immediately
      handleChangeError(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...errors]);

  // Effect to show notifications for non-exception errors
  useEffect(() => {
    if (
      error &&
      type === 'notification' &&
      api &&
      !excCodes.includes(error.code)
    ) {
      api.error({
        key: error.message,
        message: t(error.message ?? '', error.message ?? ''),
        // description: error.details,
        // duration: 2,
      });
      handleChangeError(undefined);
    }
  }, [api, error, excCodes, t, type]);

  // Determine what to render based on type and error state
  let content = null;

  if (type === 'notification') {
    content = contextHolder;
  } else if (error?.message) {
    content = (
      <StyledAlert
        style={{ borderColor: colorError }}
        message={t(error.message ?? '', error.message ?? '')}
        type="error"
        closable
        afterClose={handleClose}
      />
    );
  }

  return content;
};
